import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import {
  CheckIcon,
  GKButton,
  GKCheckbox,
  GKDatePicker,
  GKModal,
  GKSearchSelect,
  Grid2,
  Show,
  Typography,
} from "components";
import dayjs from "dayjs";
import { AutomationListObjectInterface } from "interfaces/Revenue";
import { map } from "lodash";
import { Controller, useForm } from "react-hook-form";
import {
  useCreateSingleAutomationMutation,
  useLazyGetPortfolioByFeePlanQuery,
  useUpdateSingleAutomationMutation,
} from "store/features/Revenue/ProformaApi";
import { useAppSelector } from "store/store";
import { GKModalProps } from "utils/commonInterface";
import { matchDate } from "utils/helpers";

interface Props {
  automationData: AutomationListObjectInterface;
}

const SingleAutomationModal = (props: GKModalProps & Props) => {
  const { open, setOpen, automationData } = props;

  const { feePlanList } = useAppSelector((state) => state?.app);

  const form = useForm({
    defaultValues: {
      feePlan: "",
      autoInvoiceAndFlowHistory: {
        portfolio: "",
        startingDate: "",
        isFeeCalculation: false,
        isProforma: false,
        isPaymentCollection: false,
        id: null,
      },
    },
    values: {
      feePlan: automationData?.id ? automationData?.feePlan : "",
      autoInvoiceAndFlowHistory: {
        portfolio: automationData?.id ? automationData?.portfolio : "",
        startingDate: automationData?.id ? automationData?.startingDate : "",
        isFeeCalculation: !!automationData?.isFeeCalculation,
        isProforma: !!automationData?.isProforma,
        isPaymentCollection: !!automationData?.isPaymentCollection,
        id: automationData?.id ?? null,
      },
    },
    shouldUnregister: false,
  });

  const [createSingleAutomation, { isLoading }] =
    useCreateSingleAutomationMutation();
  const [updateSingleAutomation, { isLoading: updateAutomationLoading }] =
    useUpdateSingleAutomationMutation();
  const [triggerGetPortfolios, { data, isFetching }] =
    useLazyGetPortfolioByFeePlanQuery();

  const automationSteps = [
    {
      label: "Fee Calculation",
      description: "Auto calculate management fees for the portfolio.",
      value: "isFeeCalculation",
    },
    {
      label: "Proforma Creation",
      description: "Generate draft invoices based on calculated fees.",
      value: "isProforma",
    },
    {
      label: "Payment",
      description: "Send timely email with payment links or mandates.",
      value: "isPaymentCollection",
    },
  ] as const;

  const handleCloseModal = () => setOpen(false);

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle={
        automationData?.id ? "Automation Details" : "Create New Automation"
      }
      width={automationData?.id ? "632px" : "678px"}
      maxHeight={automationData?.id && "calc(100vh - 336px)"}
      footer={
        <Grid2 size={12} container justifyContent={"space-between"}>
          <GKButton variant="outlined" color="dark" onClick={handleCloseModal}>
            Cancel
          </GKButton>
          <GKButton
            variant="contained"
            startIcon={<CheckIcon />}
            form="single-automation-form"
            type="submit"
            loading={isLoading || updateAutomationLoading}
          >
            {automationData?.id ? "Save Changes" : "Create Automation"}
          </GKButton>
        </Grid2>
      }
    >
      <form
        id="single-automation-form"
        onSubmit={form.handleSubmit(async (values) => {
          const payload = {
            feePlan: values.feePlan,
            autoInvoiceAndFlowHistory: [
              {
                ...values.autoInvoiceAndFlowHistory,
                isFeeCalculation:
                  !!values.autoInvoiceAndFlowHistory.isFeeCalculation,
                isProforma: !!values.autoInvoiceAndFlowHistory.isProforma,
                isPaymentCollection:
                  !!values.autoInvoiceAndFlowHistory.isPaymentCollection,
              },
            ],
          };

          try {
            automationData?.id
              ? await updateSingleAutomation(payload).unwrap()
              : await createSingleAutomation(payload).unwrap();

            setOpen(false);
          } catch (err) {
            console.error("Create and update single automation failed:", err);
          }
        })}
      >
        <Show.When isTrue={!automationData?.id}>
          <Grid2 container py={1} spacing={5}>
            <Grid2 container size={6} spacing={3} alignContent={"flex-start"}>
              <Grid2 size={12}>
                <Controller
                  control={form.control}
                  name="feePlan"
                  rules={{
                    required: {
                      value: true,
                      message: "Please select fee plan",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKSearchSelect
                      {...field}
                      disableClearable
                      requiredField
                      inputLabel="Fee Plan"
                      options={feePlanList}
                      onChange={async (event, val) => {
                        field.onChange(val?.value || null);

                        try {
                          await triggerGetPortfolios({
                            isUpdate: false,
                            id: val?.value,
                          }).unwrap();
                        } catch (err) {
                          console.error("Portfolio fetch failed:", err);
                        }
                      }}
                      value={
                        feePlanList.find((type) => type.value === field.value)
                          ?.name || []
                      }
                      getOptionLabel={(option) => option.name || option}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  control={form.control}
                  name="autoInvoiceAndFlowHistory.portfolio"
                  rules={{
                    required: {
                      value: true,
                      message: "Please select portfolio",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => {
                    const selected = data?.data?.find(
                      (dataItem: {
                        id: number | string;
                        portfolioName: string;
                        portfolioId: string;
                      }) => dataItem.id === field.value,
                    );

                    return (
                      <GKSearchSelect
                        {...field}
                        loading={isFetching}
                        disableClearable
                        requiredField
                        disabled={!form.watch("feePlan")}
                        inputLabel="Portfolio Name"
                        options={data?.data?.map(
                          (dataItem: {
                            portfolioName: string;
                            portfolioId: string;
                            id: number | string;
                          }) => {
                            return {
                              ...dataItem,
                              name: `${dataItem?.portfolioName} - ${dataItem?.portfolioId}`,
                              value: dataItem?.id,
                            };
                          },
                        )}
                        onChange={(event, val) =>
                          field.onChange(val?.value ?? null)
                        }
                        value={
                          selected
                            ? {
                                ...selected,
                                name: `${selected.portfolioName} - ${selected.portfolioId}`,
                                value: selected.id,
                              }
                            : null
                        }
                        getOptionLabel={(option) => option.name || option}
                        error={!!error}
                        helperText={error?.message}
                      />
                    );
                  }}
                />
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  control={form.control}
                  name="autoInvoiceAndFlowHistory.startingDate"
                  rules={{
                    required: {
                      value: true,
                      message: "Please select date",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKDatePicker
                      {...field}
                      inputLabel="First Billing Date*"
                      value={dayjs(field.value)}
                      onChange={(value) => {
                        field.onChange(dayjs(value).format("YYYY-MM-DD"));
                      }}
                      disableFuture
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid2>
            </Grid2>
            <Grid2 container size={6} spacing={2} alignContent={"flex-start"}>
              <Grid2 size={12}>
                <Typography fontWeight={700} color="var(--neutral-900)">
                  Select Automation Level
                </Typography>
              </Grid2>
              <Grid2 size={12}>
                <Timeline
                  sx={{
                    [`& .${timelineItemClasses.root}:before`]: {
                      flex: 0,
                      p: 0,
                    },
                    p: 0,
                    m: 0,
                  }}
                >
                  {map(automationSteps, (item, index) => {
                    form.watch();

                    return (
                      <TimelineItem key={item.value}>
                        <TimelineSeparator>
                          <Controller
                            name={`autoInvoiceAndFlowHistory.${item.value}`}
                            control={form.control}
                            render={({ field }) => (
                              <>
                                <GKCheckbox
                                  checked={field.value}
                                  onChange={(event) => {
                                    const { checked } = event.target;

                                    if (item.value === "isPaymentCollection") {
                                      form.setValue(
                                        "autoInvoiceAndFlowHistory.isFeeCalculation",
                                        checked,
                                      );
                                      form.setValue(
                                        "autoInvoiceAndFlowHistory.isProforma",
                                        checked,
                                      );
                                      field.onChange(checked);
                                    } else if (item.value === "isProforma") {
                                      if (checked) {
                                        form.setValue(
                                          "autoInvoiceAndFlowHistory.isFeeCalculation",
                                          true,
                                        );
                                      }
                                      field.onChange(checked);
                                    } else {
                                      field.onChange(checked);

                                      if (!checked) {
                                        form.setValue(
                                          "autoInvoiceAndFlowHistory.isProforma",
                                          false,
                                        );
                                        form.setValue(
                                          "autoInvoiceAndFlowHistory.isPaymentCollection",
                                          false,
                                        );
                                      }
                                    }
                                  }}
                                  disableLeftPadding
                                  disableRightPadding
                                />

                                {index !== automationSteps.length - 1 && (
                                  <TimelineConnector
                                    sx={{
                                      bgcolor: field.value
                                        ? "var(--primary-300)"
                                        : "var(--neutral-100)",
                                    }}
                                  />
                                )}
                              </>
                            )}
                          />
                        </TimelineSeparator>
                        <TimelineContent sx={{ p: "8px 12px" }}>
                          <Typography fontWeight={500} color="var(--grey-900)">
                            {item.label}
                          </Typography>
                          <Typography
                            mt={1}
                            fontSize={12}
                            fontWeight={500}
                            color="var(--neutral-500)"
                          >
                            {item.description}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </Timeline>
              </Grid2>
            </Grid2>
          </Grid2>
        </Show.When>
        <Show.When isTrue={!!automationData?.id}>
          <Grid2 container spacing={3}>
            <Grid2
              container
              size={12}
              bgcolor={"var(--grey-bg-100)"}
              borderRadius={2}
              p={3}
              display={"flex"}
              alignItems={"center"}
            >
              <Grid2 size={8}>
                <Typography fontSize={16} fontWeight={600} color="var(--black)">
                  {automationData?.clientName}
                </Typography>
                <Typography
                  mt={"4px"}
                  fontWeight={500}
                  color="var(--neutral-500)"
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                >
                  Fee Plan Name:
                  <Typography
                    fontWeight={500}
                    color="var(--black)"
                    component={"span"}
                  >
                    {automationData?.feePlanName}
                  </Typography>
                </Typography>
              </Grid2>
              <Grid2 size={4}>
                <Controller
                  control={form.control}
                  name="autoInvoiceAndFlowHistory.startingDate"
                  rules={{
                    required: {
                      value: true,
                      message: "Please select date",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKDatePicker
                      {...field}
                      inputLabel="First Billing Date*"
                      value={dayjs(field.value)}
                      onChange={(value) => {
                        field.onChange(dayjs(value).format("YYYY-MM-DD"));
                      }}
                      disableFuture
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid2>
            </Grid2>
            <Grid2 container size={12} spacing={2}>
              {map(
                [
                  {
                    title: "Management Fees Details",
                    lastDate: automationData?.managementLastPaymentDate,
                    nextDate: automationData?.managementNextPaymentDate,
                  },
                  {
                    title: "Performance Fees Details",
                    lastDate: automationData?.performanceLastPaymentDate,
                    nextDate: automationData?.performanceNextPaymentDate,
                  },
                ],
                (item) => (
                  <Grid2
                    key={item?.title}
                    size={6}
                    borderRadius={2}
                    border={"1px solid var(--neutral-100)"}
                    bgcolor={"var(--white)"}
                    p={"12px"}
                  >
                    <Typography fontWeight={700} color="var(--black)">
                      {item.title}
                    </Typography>
                    <Typography
                      fontSize={12}
                      fontWeight={500}
                      color="var(--neutral-700)"
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                      mt={2}
                    >
                      Last Payment Date:
                      <Typography
                        fontSize={12}
                        fontWeight={600}
                        color="var(--grey-900)"
                        component={"span"}
                      >
                        {matchDate(item.lastDate)}
                      </Typography>
                    </Typography>
                    <Typography
                      fontSize={12}
                      fontWeight={500}
                      color="var(--neutral-700)"
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                      mt={1}
                    >
                      Next Payment Date:
                      <Typography
                        fontSize={12}
                        fontWeight={600}
                        color="var(--grey-900)"
                        component={"span"}
                      >
                        {matchDate(item.nextDate)}
                      </Typography>
                    </Typography>
                  </Grid2>
                ),
              )}
            </Grid2>
            <Grid2 container size={12} spacing={2} alignContent={"flex-start"}>
              <Grid2 size={12}>
                <Typography fontWeight={700} color="var(--neutral-500)">
                  Select Automation Level
                </Typography>
              </Grid2>
              <Grid2 size={12}>
                <Timeline
                  sx={{
                    [`& .${timelineItemClasses.root}:before`]: {
                      flex: 0,
                      p: 0,
                    },
                    p: 0,
                    m: 0,
                  }}
                >
                  {map(automationSteps, (item, index) => {
                    form.watch();

                    return (
                      <TimelineItem key={item.value}>
                        <TimelineSeparator>
                          <Controller
                            name={`autoInvoiceAndFlowHistory.${item.value}`}
                            control={form.control}
                            render={({ field }) => (
                              <>
                                <GKCheckbox
                                  checked={field.value}
                                  onChange={(event) => {
                                    const { checked } = event.target;

                                    if (item.value === "isPaymentCollection") {
                                      form.setValue(
                                        "autoInvoiceAndFlowHistory.isFeeCalculation",
                                        checked,
                                      );
                                      form.setValue(
                                        "autoInvoiceAndFlowHistory.isProforma",
                                        checked,
                                      );
                                      field.onChange(checked);
                                    } else if (item.value === "isProforma") {
                                      if (checked) {
                                        form.setValue(
                                          "autoInvoiceAndFlowHistory.isFeeCalculation",
                                          true,
                                        );
                                      }
                                      field.onChange(checked);
                                    } else {
                                      field.onChange(checked);

                                      if (!checked) {
                                        form.setValue(
                                          "autoInvoiceAndFlowHistory.isProforma",
                                          false,
                                        );
                                        form.setValue(
                                          "autoInvoiceAndFlowHistory.isPaymentCollection",
                                          false,
                                        );
                                      }
                                    }
                                  }}
                                  disableLeftPadding
                                  disableRightPadding
                                />

                                {index !== automationSteps.length - 1 && (
                                  <TimelineConnector
                                    sx={{
                                      bgcolor: field.value
                                        ? "var(--primary-300)"
                                        : "var(--neutral-100)",
                                    }}
                                  />
                                )}
                              </>
                            )}
                          />
                        </TimelineSeparator>
                        <TimelineContent sx={{ p: "8px 12px" }}>
                          <Typography fontWeight={500} color="var(--grey-900)">
                            {item.label}
                          </Typography>
                          <Typography
                            mt={1}
                            fontSize={12}
                            fontWeight={500}
                            color="var(--neutral-500)"
                          >
                            {item.description}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </Timeline>
              </Grid2>
            </Grid2>
          </Grid2>
        </Show.When>
      </form>
    </GKModal>
  );
};

export default SingleAutomationModal;
